import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import CityLinks from '@src/components/CityLinks'
import type { CityLinksData } from '@src/content/cities'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/oil-recycling-page.module.css'
// eslint-disable-next-line import/extensions
import oilRecycling from '../../../json/oil-recycling-data.json'

const oilServiceData = {
  name: 'Oil Recycling and Used Oil Pickup',
  description:
    'Eco-friendly used oil recycling and pickup for businesses. We handle used cooking oil, waste motor oil, hydraulic and transmission oil via container removal or on-site pumping — DOT and RCRA compliant, with instant online pricing.',
  serviceType: 'Oil Recycling and Used Oil Disposal',
  url: 'https://getdimension.com/oil-recycling',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
}

const META_DESCRIPTION =
  'Used oil recycling and pickup for businesses — cooking oil, motor oil, hydraulic ' +
  'oil. Container removal or on-site pumping, DOT & RCRA compliant, with instant ' +
  'online quotes.'

const oilBreadcrumbs = {
  items: [
    { name: 'Home', url: 'https://getdimension.com' },
    { name: 'Regulated Waste Disposal', url: 'https://getdimension.com/regulated-waste-disposal' },
    { name: 'Oil Recycling', url: 'https://getdimension.com/oil-recycling' },
  ],
}

const oilFAQData = {
  questions: oilRecycling.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

export type OilRecyclingPageProps = {
  /**
   * Optional content for the right-hand hero column (e.g. the n8n chatbot
   * embed). When omitted, the default CTA card is shown.
   */
  heroAside?: React.ReactNode
  /**
   * Extra class for the right-hand hero column wrapper. `/oil-recycling` uses it
   * to reserve height while the chatbot A/B test decides what goes in there.
   */
  ctaContainerClassName?: string
  /**
   * When true, the page is excluded from search indexing and canonicalized to
   * the primary `/oil-recycling` page (used by the chatbot test variant).
   */
  noindex?: boolean
  /**
   * From `getCityLinks('oil-recycling')` in the route's `getStaticProps`. Passed
   * in rather than read here so the city registry stays out of the client bundle.
   */
  cityLinks?: CityLinksData | null
}

function OilRecyclingPage({
  heroAside,
  ctaContainerClassName,
  noindex = false,
  cityLinks = null,
}: OilRecyclingPageProps) {
  return (
    <>
      <Head>
        <title>Oil Recycling &amp; Used Oil Pickup for Businesses | Dimension</title>
        <meta name='description' content={META_DESCRIPTION} />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        {noindex && <meta name='robots' content='noindex, follow' />}
        <link rel='canonical' href='https://getdimension.com/oil-recycling' />
        <SchemaMarkup type='WebSite' />
        <SchemaMarkup type='Service' data={oilServiceData} />
        <SchemaMarkup type='FAQPage' data={oilFAQData} />
        <SchemaMarkup type='LocalBusiness' />
        <SchemaMarkup type='BreadcrumbList' data={oilBreadcrumbs} />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant='hazardous-waste'
          heading={oilRecycling.pitch.heading}
          material={oilRecycling.pitch.material}
          pitchText={oilRecycling.pitch.pitchText}
          ctaLink={oilRecycling.pitch.ctaLink}
          ctaText={oilRecycling.pitch.ctaText}
          heroAside={heroAside}
          ctaContainerClassName={ctaContainerClassName}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...oilRecycling.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...oilRecycling.benefits} />
        <CaseStudy {...oilRecycling.caseStudy} />
        <Approach {...oilRecycling.approach} />
        <FAQ {...oilRecycling.FAQContent} />
        <CityLinks data={cityLinks} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Paint Recycling and Disposal',
              href: '/paint-disposal',
              description:
                'Compliant pickup and recycling of latex, oil-based, and spray paint for businesses.',
            },
            {
              label: 'Business Waste Solutions',
              href: '/b2b-solutions',
              description:
                'Customized waste management for enterprises with dedicated reporting and account management.',
            },
            {
              label: 'Sustainability',
              href: '/sustainability',
              description:
                'Learn how Dimension diverts waste from landfills through recycling and responsible disposal.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default OilRecyclingPage
